import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import './ResetPassword.css';

const APP_DEEP_LINK = 'assetpal://';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isResetting, setIsResetting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
  });

  useEffect(() => {
    checkResetToken();
  }, []);

  const checkResetToken = async () => {
    console.log('🔍 Full URL:', window.location.href);

    const searchParams = new URLSearchParams(window.location.search);
    const code       = searchParams.get('code');
    const tokenHash  = searchParams.get('token_hash');
    const token      = searchParams.get('token');
    const type       = searchParams.get('type');
    const error      = searchParams.get('error');
    const errorDesc  = searchParams.get('error_description');

    // ── 1. Error returned by Supabase ──────────────────────────────────────
    if (error) {
      console.error('❌ Supabase error:', error, errorDesc);
      setMessage({ text: `Error: ${errorDesc || error}`, type: 'error' });
      setIsValidToken(false);
      return;
    }

    // ── 2. Already have a session (e.g. navigated back) ───────────────────
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      console.log('✅ Active session:', session.user?.email);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // ── 3. token_hash flow (stateless — works cross-browser) ──────────────
    //    Supabase sends ?token_hash=...&type=recovery when using the
    //    email OTP / magic-link flow (no PKCE verifier needed).
    const hashToVerify = tokenHash || token || code;
    if (hashToVerify) {
      console.log('� Verifying token hash...');
      try {
        const { data, error: otpError } = await supabase.auth.verifyOtp({
          token_hash: hashToVerify,
          type: type === 'recovery' ? 'recovery' : 'recovery',
        });
        if (otpError) {
          console.error('❌ verifyOtp failed:', otpError);
          setMessage({ text: 'Invalid or expired reset link. Please request a new password reset.', type: 'error' });
          setIsValidToken(false);
        } else {
          console.log('✅ Token verified, session active:', data.user?.email);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('❌ verifyOtp exception:', err);
        setMessage({ text: 'Something went wrong. Please request a new password reset.', type: 'error' });
        setIsValidToken(false);
      }
      return;
    }

    // ── 4. Hash fragment fallback (#access_token=...) ─────────────────────
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken  = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    if (accessToken) {
      console.log('🔄 Legacy hash token flow...');
      try {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || accessToken,
        });
        if (sessionError) {
          console.error('❌ setSession failed:', sessionError);
          setMessage({ text: `Failed to verify reset link: ${sessionError.message}`, type: 'error' });
          setIsValidToken(false);
        } else {
          console.log('✅ Legacy session set');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error('❌ setSession exception:', err);
        setMessage({ text: `An error occurred: ${err.message}`, type: 'error' });
        setIsValidToken(false);
      }
      return;
    }

    // ── 5. Nothing found ──────────────────────────────────────────────────
    console.error('❌ No token, code, or session found in URL');
    setMessage({ text: 'Invalid or expired reset link. Please request a new password reset.', type: 'error' });
    setIsValidToken(false);
  };

  const validatePassword = (pwd) => {
    setRequirements({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
    });
  };

  const isPasswordValid = () => {
    return Object.values(requirements).every(req => req === true);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isResetting) return;

    if (password !== confirmPassword) {
      setMessage({ text: 'Passwords do not match. Please try again.', type: 'error' });
      return;
    }

    if (!isPasswordValid()) {
      setMessage({ 
        text: 'Password does not meet requirements. Please check the criteria below.', 
        type: 'error' 
      });
      return;
    }

    setIsResetting(true);
    setMessage({ text: '', type: '' });

    try {
      console.log('🔄 Attempting to reset password...');

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      console.log('✅ Password reset successful');
      setIsSuccess(true);
      
      // Sign out after successful reset
      await supabase.auth.signOut();
    } catch (error) {
      console.error('❌ Password reset failed:', error);
      setMessage({ text: `Failed to reset password: ${error.message}`, type: 'error' });
    } finally {
      setIsResetting(false);
    }
  };

  const openApp = () => {
    window.location.href = APP_DEEP_LINK;
  };

  if (isSuccess) {
    return (
      <div className="reset-container">
        <div className="reset-card">
          <div className="success-icon">✅</div>
          <h2 className="success-title">Password Reset Successfully!</h2>
          <p className="success-text">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <button className="reset-button" onClick={openApp}>
            Open AssetPal App
          </button>
          <div className="footer">
            <a href={APP_DEEP_LINK} className="footer-link">Return to App</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-container">
      <div className="reset-card">
        <div className="logo">
          <h1>🔐 AssetPal</h1>
          <p>Reset Your Password</p>
        </div>

        {message.text && (
          <div className={`message ${message.type} show`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your new password"
              required
              minLength="8"
              disabled={!isValidToken}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              type="password"
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              required
              minLength="8"
              disabled={!isValidToken}
              style={{
                borderColor: confirmPassword.length > 0 && password !== confirmPassword ? '#ef4444' : '#e5e7eb'
              }}
            />
          </div>

          <div className="password-requirements">
            <strong style={{ color: '#374151' }}>Password must contain:</strong>
            <ul>
              <li className={requirements.length ? 'valid' : 'invalid'}>
                At least 8 characters
              </li>
              <li className={requirements.uppercase ? 'valid' : 'invalid'}>
                One uppercase letter
              </li>
              <li className={requirements.lowercase ? 'valid' : 'invalid'}>
                One lowercase letter
              </li>
              <li className={requirements.number ? 'valid' : 'invalid'}>
                One number
              </li>
            </ul>
          </div>

          <button 
            type="submit" 
            className="reset-button"
            disabled={!isValidToken || isResetting}
          >
            {isResetting ? (
              <>
                <span className="spinner"></span> Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        <div className="footer">
          Return to <a href={APP_DEEP_LINK} className="footer-link">AssetPal App</a>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
